import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Flag, PawPrint, Star, Trash2, Undo2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { formatDate, formatMoney, formatNumber } from "@/lib/utils";
import { useAuthStore } from "@/stores/authStore";
import { useListQuery } from "@/hooks/useListQuery";
import { DataTable, type Column } from "@/components/data/DataTable";
import {
  ActiveFilters,
  BulkActionBar,
  ExportButton,
  FilterSelect,
  PageHeader,
  SearchInput,
  Toolbar,
} from "@/components/data/toolbar";
import { Badge, EmptyState } from "@/components/ui/primitives";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/data/ConfirmDialog";
import { LISTING_STATUS_BADGE } from "@/features/users/shared";

interface ListingRow {
  id: string;
  name: string;
  breed: string;
  type: string;
  gender: string | null;
  price: string | number;
  admin_status: string;
  is_featured: boolean;
  moderation_state: string;
  ai_moderation_score: number | null;
  photos: string[];
  created_at: string;
  breeder: { id: string; name: string; city: string | null; verified: boolean };
  _count: { reports: number; favorites: number; contacts_unlocked: number };
}

const FILTERS = ["status", "type", "breed", "city", "featured", "flagged"] as const;

export const ListingsListPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const can = useAuthStore((s) => s.can);

  const [selected, setSelected] = React.useState<string[]>([]);
  const [bulk, setBulk] = React.useState<
    null | "remove" | "restore" | "feature" | "unfeature" | "flag"
  >(null);

  const list = useListQuery<ListingRow>({
    key: "listings",
    path: "/listings",
    filterKeys: FILTERS,
    defaultSort: "created_at",
  });

  const facets = useQuery({
    queryKey: ["listings", "facets"],
    queryFn: () =>
      api.get<{
        breeds: { value: string; count: number }[];
        types: { value: string; count: number }[];
        cities: { value: string; count: number }[];
        statuses: { value: string; count: number }[];
      }>("/listings/facets"),
    staleTime: 5 * 60_000,
  });

  const runBulk = useMutation({
    mutationFn: ({ action, reason }: { action: string; reason: string }) =>
      api.postWithMessage<{ affected: number; missing: string[] }>(
        "/listings/bulk",
        { ids: selected, action, reason: reason || undefined },
      ),
    onSuccess: ({ message }) => {
      toast.success(message);
      setSelected([]);
      setBulk(null);
      void queryClient.invalidateQueries({ queryKey: ["listings"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard", "kpis"] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Bulk action failed"),
  });

  const columns: Column<ListingRow>[] = [
    {
      key: "name",
      header: "Listing",
      sortable: true,
      render: (l) => (
        <div className="flex items-center gap-2.5">
          {l.photos?.[0] ? (
            <img
              src={l.photos[0]}
              alt=""
              loading="lazy"
              className="h-9 w-9 shrink-0 rounded-md object-cover"
            />
          ) : (
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-surface text-xs">
              🐾
            </span>
          )}
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 truncate font-medium">
              {l.name}
              {l.is_featured && (
                <Star className="h-3 w-3 shrink-0 text-warning" aria-label="Featured" />
              )}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {l.breed} · {l.type}
              {l.gender && ` · ${l.gender}`}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: "breeder",
      header: "Breeder",
      hideOnMobile: true,
      render: (l) => (
        <div className="min-w-0">
          <p className="truncate text-sm">{l.breeder?.name ?? "—"}</p>
          <p className="truncate text-xs text-muted-foreground">
            {l.breeder?.city ?? "—"}
            {l.breeder?.verified && " · verified"}
          </p>
        </div>
      ),
    },
    {
      key: "price",
      header: "Price",
      sortable: true,
      align: "right",
      render: (l) => <span className="numeric">{formatMoney(l.price)}</span>,
    },
    {
      key: "admin_status",
      header: "Status",
      render: (l) => {
        const status = l.admin_status ?? (l as any).status ?? "available";
        const cfg = LISTING_STATUS_BADGE[status];
        return (
          <div className="flex flex-wrap items-center gap-1">
            <Badge variant={cfg?.variant ?? "secondary"}>
              {cfg?.label ?? status}
            </Badge>
            {l.moderation_state === "flagged" && (
              <Badge variant="warning">Flagged</Badge>
            )}
          </div>
        );
      },
    },
    {
      key: "engagement",
      header: "Engagement",
      align: "right",
      hideOnMobile: true,
      render: (l) => {
        const unlocks = l._count?.contacts_unlocked ?? 0;
        const reports = l._count?.reports ?? 0;
        return (
          <div className="text-xs text-muted-foreground">
            <span className="numeric">{formatNumber(unlocks)}</span>{" "}
            unlocks
            {reports > 0 && (
              <span className="ml-2 text-destructive">
                {reports} report{reports === 1 ? "" : "s"}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: "created_at",
      header: "Listed",
      sortable: true,
      align: "right",
      render: (l) => (
        <span className="text-xs text-muted-foreground">
          {formatDate(l.created_at)}
        </span>
      ),
    },
  ];

  const activeFilters = [
    list.q ? { key: "q", label: `“${list.q}”` } : null,
    ...FILTERS.map((f) =>
      list.filters[f] ? { key: f, label: `${f}: ${list.filters[f]}` } : null,
    ),
  ].filter(Boolean) as { key: string; label: string }[];

  const bulkCopy: Record<string, { title: string; desc: string; label: string; destructive: boolean; reason: boolean }> = {
    remove: {
      title: `Remove ${selected.length} listing(s)?`,
      desc: "They disappear from the app immediately. You can restore them later.",
      label: "Remove listings",
      destructive: true,
      reason: true,
    },
    restore: {
      title: `Restore ${selected.length} listing(s)?`,
      desc: "They become visible in the app again.",
      label: "Restore listings",
      destructive: false,
      reason: false,
    },
    feature: {
      title: `Feature ${selected.length} listing(s)?`,
      desc: "Featured listings are surfaced more prominently in the app.",
      label: "Feature listings",
      destructive: false,
      reason: false,
    },
    unfeature: {
      title: `Remove ${selected.length} listing(s) from featured?`,
      desc: "They return to normal ranking.",
      label: "Unfeature",
      destructive: false,
      reason: false,
    },
    flag: {
      title: `Flag ${selected.length} listing(s) for review?`,
      desc: "They are marked for moderation and hidden from browse until reviewed.",
      label: "Flag listings",
      destructive: true,
      reason: true,
    },
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Listings"
        description="Every pet listed on the marketplace."
        actions={
          can("listings:export") && (
            <ExportButton
              path="/listings/export"
              query={{ format: "xlsx", ...list.filters, q: list.q || undefined }}
              label="Export"
            />
          )
        }
      />

      <Toolbar>
        <SearchInput
          value={list.q}
          onChange={list.setQ}
          placeholder="Search name, breed, breeder…"
          className="w-full sm:w-72"
        />
        <FilterSelect
          label="Status"
          value={list.filters.status}
          onChange={(v) => list.setFilter("status", v)}
          options={(facets.data?.statuses ?? []).map((s) => ({
            value: s.value,
            label: LISTING_STATUS_BADGE[s.value]?.label ?? s.value,
            count: s.count,
          }))}
        />
        <FilterSelect
          label="Type"
          value={list.filters.type}
          onChange={(v) => list.setFilter("type", v)}
          options={(facets.data?.types ?? []).map((t) => ({
            value: t.value,
            label: t.value,
            count: t.count,
          }))}
        />
        <FilterSelect
          label="Breed"
          value={list.filters.breed}
          onChange={(v) => list.setFilter("breed", v)}
          options={(facets.data?.breeds ?? []).slice(0, 40).map((b) => ({
            value: b.value,
            label: b.value,
            count: b.count,
          }))}
        />
        <FilterSelect
          label="City"
          value={list.filters.city}
          onChange={(v) => list.setFilter("city", v)}
          options={(facets.data?.cities ?? []).slice(0, 40).map((c) => ({
            value: c.value,
            label: c.value,
            count: c.count,
          }))}
        />
        <span className="ml-auto text-sm text-muted-foreground">
          {formatNumber(list.total)} total
        </span>
      </Toolbar>

      <ActiveFilters
        filters={activeFilters}
        onClear={(key) =>
          key === "q" ? list.setQ("") : list.setFilter(key, undefined)
        }
        onClearAll={list.clearFilters}
      />

      {can("listings:bulk_action") && (
        <BulkActionBar count={selected.length} onClear={() => setSelected([])}>
          {can("listings:remove") && (
            <Button variant="danger" size="sm" onClick={() => setBulk("remove")}>
              <Trash2 aria-hidden />
              Remove
            </Button>
          )}
          {can("listings:restore") && (
            <Button variant="outline" size="sm" onClick={() => setBulk("restore")}>
              <Undo2 aria-hidden />
              Restore
            </Button>
          )}
          {can("listings:feature") && (
            <>
              <Button variant="outline" size="sm" onClick={() => setBulk("feature")}>
                <Star aria-hidden />
                Feature
              </Button>
              <Button variant="outline" size="sm" onClick={() => setBulk("unfeature")}>
                Unfeature
              </Button>
            </>
          )}
          {can("listings:flag") && (
            <Button variant="outline" size="sm" onClick={() => setBulk("flag")}>
              <Flag aria-hidden />
              Flag
            </Button>
          )}
        </BulkActionBar>
      )}

      {list.error ? (
        <EmptyState
          icon={PawPrint}
          title="Could not load listings"
          description={list.error instanceof Error ? list.error.message : undefined}
        />
      ) : (
        <DataTable
          caption="All listings with breeder, price, status, and engagement"
          columns={columns}
          rows={list.rows}
          rowKey={(l) => l.id}
          loading={list.loading}
          sort={list.sort}
          order={list.order}
          onSortChange={list.setSort}
          onRowClick={(l) => navigate(`/listings/${l.id}`)}
          selectable={can("listings:bulk_action")}
          selectedIds={selected}
          onSelectionChange={setSelected}
          total={list.total}
          nextCursor={list.nextCursor}
          onLoadMore={list.loadMore}
          loadingMore={list.loadingMore}
          emptyTitle={
            list.activeFilterCount > 0
              ? "No listings match those filters"
              : "No listings yet"
          }
        />
      )}

      {bulk && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setBulk(null)}
          title={bulkCopy[bulk].title}
          description={bulkCopy[bulk].desc}
          confirmLabel={bulkCopy[bulk].label}
          variant={bulkCopy[bulk].destructive ? "destructive" : "default"}
          requireReason={bulkCopy[bulk].reason}
          loading={runBulk.isPending}
          onConfirm={(reason) => runBulk.mutate({ action: bulk, reason })}
        />
      )}
    </div>
  );
};
